'use client';

import * as React from 'react';
import { closeNonModalLayers, topLayer } from './layerStack';

/**
 * A press or a key inside an embedded page (an <iframe>, e.g. the LiveKit dashboard) never reaches this document: no
 * pointerdown for "outside press", no keydown for Escape, no focusin. A header menu or popover that was open stayed open
 * and, because the keyboard now belongs to the frame, Escape could not close it either (admin report D8, /adminpanel/livekit).
 *
 * When focus moves into a frame, the window blurs and `document.activeElement` is the frame's element. That IS an outside
 * press, so we send the one the document did not get: a primary pointerdown on <body>. Radix menus/selects/popovers and the
 * layer stack (lib/layers/layerStack.ts) already treat that as "outside" and close their non-modal layers.
 *
 * Left alone: a frame inside a popup or dialog (its own content), and any open modal (a modal is not dismissed by what
 * happens behind it).
 */
export function dismissOnFrameFocus(doc: Document = document): boolean {
  const active = doc.activeElement;
  if (!active || active.tagName !== 'IFRAME') return false;
  if (active.closest('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]')) return false;
  if (doc.querySelector('[aria-modal="true"]') || topLayer()?.modal) return false;
  const view = doc.defaultView;
  if (!view) return false;
  const dismissed = topLayer();
  doc.body.dispatchEvent(new view.PointerEvent('pointerdown', { bubbles: true, cancelable: true, composed: true, isPrimary: true, button: 0, pointerType: 'mouse' }));
  // The pointer closes the top stack entry; dismiss any lower popups too, exactly once.
  closeNonModalLayers('outside-pointer', dismissed);
  return true;
}

export function FrameFocusDismiss(): null {
  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onBlur = () => {
      // The frame is the active element once the blur has settled.
      clearTimeout(timer);
      timer = setTimeout(() => { dismissOnFrameFocus(document); }, 0);
    };
    window.addEventListener('blur', onBlur);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  return null;
}
